import React from "react"
import { graphql } from "gatsby"

import { SEO, Subheader } from "components"



import {
  Accordion,
  CareerCards,
  CareerDetails,
  CareerDropdown,
  CareerBoard,
  CareerGrid,
  Collage,
  ColumnGrid,
  Gallery,
  LocationList,
  SectionHeader,
  SideBySide,
  TalkingShopFeed,
  Testimonial,
  TextBlock,
  WhyPageCards,
} from "slices"

import Hero from "./_Hero"



/**
 * Careers Component
 * Describes the parent career page.
 * Defines the contentful components that should be diplayed on each URL slug
 * under the careers page.
 * All JobFeed API calls take place at this parent component
 * and data is passed down to the component children
 *
 * @param  {object} {data
 * @param  {object} pageContext
 * @param  {object} location}
 */
function Careers({ data, pageContext}) {
  const { contentfulCareersPage } = data
  
  if (contentfulCareersPage) {

    /*
   * Create typename ContentfulCareerBoard to help diplay job listing and form details
   * @param  {object} listing
   */
  if (contentfulCareersPage.slug === "job-board") {
    contentfulCareersPage.content = [{ __typename: "ContentfulCareerBoard" }]
  } else if (contentfulCareersPage.slug === "job-details") {
    contentfulCareersPage.content[0] = { __typename: "ContentfulCareerDetails" }
  }
    
    return (
      <>
        <SEO
          title={contentfulCareersPage?.seoTitle}
          description={contentfulCareersPage?.seoDescription?.internal?.content}
          image={contentfulCareersPage?.seoImage}
        />
        <main>
          <Subheader {...contentfulCareersPage?.subnav} />
          <Hero {...contentfulCareersPage} />

          {contentfulCareersPage?.content?.map(
                ({ __typename, ...rest }, i) => {
                  switch (__typename) {
                    case "ContentfulAccordion": {
                      return (
                        <Accordion {...rest} key={`content-accordion--${i}`} />
                      )
                    }
                    case "ContentfulCareerCards": {
                      return (
                        <CareerCards
                          {...rest}
                          key={`content-careerCards--${i}`}
                        />
                      )
                    }
                    case "ContentfulCareerDetails": {
                      return (
                        <CareerDetails
                          {...rest}
                          key={`content-careerDetails--${i}`}
                        />
                      )
                    }
                    case "ContentfulCareerDropdown": {
                      return (
                        <CareerDropdown
                          {...rest}
                          key={`content-careerDropdown--${i}`}
                        />
                      )
                    }

                    case "ContentfulCareerBoard": {
                      return (
                        <CareerBoard
                          {...rest}
                          key={`content-CareerBoard--${i}`}
                        />
                      )
                    }

                    case "ContentfulCareerGrid": {
                      return (
                        <CareerGrid
                          {...rest}
                          key={`content-careerGrid--${i}`}
                        />
                      )
                    }
                    case "ContentfulCollage": {
                      return <Collage {...rest} key={`content-collage--${i}`} />
                    }
                    case "ContentfulColumnGrid": {
                      return (
                        <ColumnGrid
                          {...rest}
                          key={`content-columnGrid--${i}`}
                        />
                      )
                    }
                    case "ContentfulGallery": {
                      return <Gallery {...rest} key={`content-gallery--${i}`} />
                    }
                    case "ContentfulLocationList": {
                      return (
                        <LocationList
                          {...rest}
                          key={`content-locationList--${i}`}
                        />
                      )
                    }
                    case "ContentfulSectionHeader": {
                      return (
                        <SectionHeader
                          {...rest}
                          key={`content-sectionHeader--${i}`}
                        />
                      )
                    }
                    case "ContentfulSideBySide": {
                      return (
                        <SideBySide
                          {...rest}
                          key={`content-sideBySide--${i}`}
                        />
                      )
                    }
                    case "ContentfulTalkingShopFeed": {
                      return (
                        <TalkingShopFeed
                          {...rest}
                          feeds={pageContext.talkingShopFeeds}
                          key={`content-talkingShopFeed--${i}`}
                        />
                      )
                    }
                    case "ContentfulTestimonial": {
                      return (
                        <Testimonial
                          {...rest}
                          key={`content-testimonial--${i}`}
                        />
                      )
                    }
                    case "ContentfulTextBlock": {
                      return (
                        <TextBlock {...rest} key={`content-textBlock--${i}`} />
                      )
                    }
                    case "ContentfulWhyPageCards": {
                      return (
                        <WhyPageCards
                          {...rest}
                          key={`content-whyPageCards--${i}`}
                        />
                      )
                    }
                    default:
                      return null
                  }
                }
              )}
        </main>
      </>
    )
  }
  return null
}

export const pageQuery = graphql`
  query CareersQuery($slug: String) {
    contentfulCareersPage(slug: { eq: $slug }) {
      seoTitle
      slug
      seoDescription {
        internal {
          content
        }
      }
      seoImage {
        ...AssetFragment
      }
      heroHeader
      heroDecorator {
        ...DecoratorFragment
      }
      heroContent {
        raw
      }
      heroImage {
        ...AssetFragment
        gatsbyImageData(width: 1024, layout: FULL_WIDTH, placeholder: BLURRED)
      }
      heroButton {
        ...LinkFragment
      }
      subnav {
        title
        links {
          ...LinkFragment
        }
      }
      content {
        __typename
        ... on ContentfulAccordion {
          content {
            raw
          }
          accordionItems {
            title
            content {
              raw
            }
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulCareerCards {
          title
          decorators {
            ...DecoratorFragment
          }
          pages {
            heroHeader
            heroImage {
              ...AssetFragment
              gatsbyImageData(width: 768)
            }
            slug
          }
        }
        ... on ContentfulCareerDropdown {
          heading
          subheading {
            raw
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulCareerGrid {
          careerCategories {
            name
            description {
              raw
            }
            image {
              ...AssetFragment
              small: gatsbyImageData(
                width: 420
                placeholder: BLURRED
                quality: 100
              )
              large: gatsbyImageData(
                width: 768
                placeholder: BLURRED
                quality: 100
              )
            }
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulCollage {
          collageText
          content {
            raw
          }
          images {
            ...AssetFragment
            gatsbyImageData(width: 227, aspectRatio: 1, placeholder: BLURRED)
          }
        }
        ... on ContentfulColumnGrid {
          align
          heading
          subheading {
            raw
          }
          linkCard {
            slug
            category {
              name
              slug
            }
            title
            image {
              ...AssetFragment
              gatsbyImageData(width: 720, layout: FULL_WIDTH)
            }
          }
          columnGridCards {
            color
            image {
              ...AssetFragment
              gatsbyImageData(width: 256, layout: FULL_WIDTH)
            }
            text {
              raw
            }
          }
        }
        ... on ContentfulGallery {
          galleryVideo
          images {
            ...AssetFragment
            small: gatsbyImageData(width: 358, placeholder: BLURRED)
            large: gatsbyImageData(width: 810, placeholder: BLURRED)
          }
        }
        ... on ContentfulSectionHeader {
          header: heading {
            heading
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulSideBySide {
          heading
          image {
            ...AssetFragment
            gatsbyImageData(width: 600, placeholder: BLURRED, quality: 80)
          }
          decorators {
            ...DecoratorFragment
          }
          content {
            raw
          }
        }
        ... on ContentfulTalkingShopFeed {
          content {
            raw
          }
          type {
            slug
            name
          }
        }
        ... on ContentfulTestimonial {
          quote {
            quote {
              internal {
                content
              }
            }
            attribution
          }
          image {
            ...AssetFragment
            gatsbyImageData(width: 768, placeholder: BLURRED)
          }
          video {
            ...AssetFragment
          }
          link {
            label
            link {
              internal {
                content
              }
            }
          }
        }
        ... on ContentfulTextBlock {
          content {
            raw
          }
          button {
            ...LinkFragment
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulWhyPageCards {
          title
          size
          links {
            slug
            heroHeading
            heroImage {
              ...AssetFragment
              gatsbyImageData(width: 600)
            }
            heroVideo {
              ...AssetFragment
            }
          }
        }
      }
    }
  }
`

export default Careers
